import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ShipSpeed, ShipSpeedRow } from 'models/ShipBobPerformanceModel';

function formatPct(value: number | null): string {
  return value == null ? '—' : `${value.toFixed(1)}%`;
}

function speedColor(value: number | null): string {
  if (value == null) return '#64748B';
  if (value >= 95) return '#15803D';
  if (value >= 85) return '#B45309';
  return '#B91C1C';
}

function SpeedTable({
  title,
  nameHeader,
  rows,
  showZone = false,
}: {
  title: string;
  nameHeader: string;
  rows: ShipSpeedRow[];
  showZone?: boolean;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 340 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{nameHeader}</TableCell>
              <TableCell align="right">Orders</TableCell>
              <TableCell align="right">Shipments</TableCell>
              <TableCell align="right">Volume</TableCell>
              <TableCell align="right">Cost/Order</TableCell>
              {showZone && (
                <TableCell align="right">
                  <Tooltip title="Average carrier zone of this FC's domestic shipments (1 = local, 8 = cross-country). Higher avg zone = customers farther from the warehouse = higher cost & slower transit — an inventory-placement signal, not a rate problem.">
                    <span>Avg zone</span>
                  </Tooltip>
                </TableCell>
              )}
              <TableCell align="right">
                <Tooltip title="Average calendar days from ship to delivery (delivered shipments only).">
                  <span>Transit (d)</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">Avg BD</TableCell>
              <TableCell align="right">≤1 BD</TableCell>
              <TableCell align="right">≤2 BD</TableCell>
              <TableCell align="right">≤3 BD</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.name} hover>
                <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                <TableCell align="right">
                  {row.orders == null ? '—' : row.orders.toLocaleString()}
                </TableCell>
                <TableCell align="right">
                  {row.shipments.toLocaleString()}
                </TableCell>
                <TableCell align="right">
                  {row.volumeSharePct == null
                    ? '—'
                    : `${row.volumeSharePct.toFixed(1)}%`}
                </TableCell>
                <TableCell align="right">
                  {row.costPerOrder == null
                    ? '—'
                    : `$${row.costPerOrder.toFixed(2)}`}
                </TableCell>
                {showZone && (
                  <TableCell align="right">
                    {row.avgZone == null ? '—' : row.avgZone.toFixed(1)}
                  </TableCell>
                )}
                <TableCell align="right">
                  {row.avgTransitDays == null
                    ? '—'
                    : row.avgTransitDays.toFixed(2)}
                </TableCell>
                <TableCell align="right">
                  {row.avgBusinessDays == null
                    ? '—'
                    : row.avgBusinessDays.toFixed(2)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: speedColor(row.within1Pct), fontWeight: 600 }}
                >
                  {formatPct(row.within1Pct)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: speedColor(row.within2Pct), fontWeight: 600 }}
                >
                  {formatPct(row.within2Pct)}
                </TableCell>
                <TableCell align="right">{formatPct(row.within3Pct)}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={showZone ? 11 : 10} sx={{ color: '#94A3B8' }}>
                  No data for this range.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function ShipSpeedTables({ shipSpeed }: { shipSpeed: ShipSpeed }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Performance by fulfillment center &amp; brand
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748B' }}>
          Ship speed (business days from order-created → shipped; weekends
          excluded, holidays not modeled — our own calc, not ShipBob&apos;s SLA
          grading) alongside volume &amp; cost per order.
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <SpeedTable
          title="By fulfillment center"
          nameHeader="Fulfillment center"
          rows={shipSpeed.byFulfillmentCenter}
          showZone
        />
        <SpeedTable
          title="By brand"
          nameHeader="Brand"
          rows={shipSpeed.byBrand}
        />
      </Box>
    </Box>
  );
}
